/** Reporting engine — click-to-sort ticket tables.
 *
 * Shared by the board's "Recent tickets" tile and the drill-down's own table
 * (issue #121), which is why it lives here rather than in either: two copies of
 * a comparator is how the same column comes to sort two different ways on two
 * surfaces.
 *
 * A column declares how to READ its value and how to RENDER its cell, so a
 * header and the cells under it cannot drift apart. Columns with a natural
 * ranking (priority, satisfaction) sort by the order the design already
 * declares — PRIORITY_ORDER, CSAT_TIER_ORDER — not alphabetically, so one click
 * on Priority puts Critical on top instead of "Critical, High, Low, Medium".
 *
 * Sorting is client-side over the rows already in hand. Both tables are bounded
 * (the tile shows eight, the drill-down one month's cohort), so there is nothing
 * to page and no request to make.
 */

import { useCallback, useMemo, useState } from "react";
import type { CSSProperties, ReactNode } from "react";

import { C } from "./data";

export type SortValue = string | number | null;
export type SortDir = "asc" | "desc";

export interface Column<T> {
  key: string;
  label: string;
  /** Sort key for a row. Omit to make the column unsortable (no header button).
   *  Return null for "no value" — those rows sort last in BOTH directions, so
   *  reversing the order never fills the top of the table with blanks. */
  value?: (row: T) => SortValue;
  cell: (row: T) => ReactNode;
  /** Right-aligns the header and its cells (the numeric columns). */
  right?: boolean;
  /** Per-column cell style: mono, nowrap, muted. Merged over the base td. */
  cellStyle?: CSSProperties;
}

function compare(a: SortValue, b: SortValue): number {
  if (typeof a === "number" && typeof b === "number") return a - b;
  // `numeric` so ticket references order as a human reads them (T20260810.0029
  // after T20260810.0003, not lexicographically among differing digit counts).
  return String(a).localeCompare(String(b), undefined, { numeric: true });
}

/** Rows in the requested order, plus the state a header needs to render itself.
 *
 * With no sort chosen the caller's own order is returned untouched — each table
 * has a meaningful default (newest first), and a third click on a header returns
 * to it rather than leaving the reader stuck in an ordering they cannot undo. */
export function useColumnSort<T>(rows: T[], columns: Column<T>[]) {
  const [sort, setSort] = useState<{ key: string; dir: SortDir } | null>(null);

  const toggle = useCallback((key: string) => {
    setSort((s) => {
      if (s?.key !== key) return { key, dir: "asc" };
      return s.dir === "asc" ? { key, dir: "desc" } : null;
    });
  }, []);

  const sorted = useMemo(() => {
    if (!sort) return rows;
    const read = columns.find((c) => c.key === sort.key)?.value;
    if (!read) return rows;
    // Array.prototype.sort is stable, so rows that tie on this column keep the
    // table's default order underneath the sort.
    return rows.slice().sort((ra, rb) => {
      const a = read(ra);
      const b = read(rb);
      if (a == null || b == null) return a == null && b == null ? 0 : a == null ? 1 : -1;
      return sort.dir === "asc" ? compare(a, b) : -compare(a, b);
    });
  }, [rows, columns, sort]);

  return { sorted, sort, toggle };
}

const sortButton: CSSProperties = {
  font: "inherit",
  color: "inherit",
  letterSpacing: "inherit",
  textTransform: "inherit",
  textAlign: "inherit",
  background: "none",
  border: "none",
  padding: 0,
  margin: 0,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  whiteSpace: "nowrap",
};

function SortHeader<T>({
  col,
  sort,
  onToggle,
  style,
}: {
  col: Column<T>;
  sort: { key: string; dir: SortDir } | null;
  onToggle: (key: string) => void;
  style: CSSProperties;
}) {
  const th: CSSProperties = { ...style, textAlign: col.right ? "right" : "left" };
  if (!col.value) return <th style={th}>{col.label}</th>;
  const active = sort?.key === col.key;
  return (
    <th style={th} aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}>
      <button
        type="button"
        style={sortButton}
        title={`Sort by ${col.label.toLowerCase()}`}
        // The board wraps every widget in a card that opens the drill-down when
        // clicked, so a header click has to stop there or sorting the tile would
        // also pop the widget open.
        onClick={(e) => {
          e.stopPropagation();
          onToggle(col.key);
        }}
      >
        {col.label}
        <span
          aria-hidden="true"
          style={{ color: active ? C.ink : C.stone, opacity: active ? 1 : 0.4, fontSize: "0.9em" }}
        >
          {active ? (sort.dir === "asc" ? "▲" : "▼") : "↕"}
        </span>
      </button>
    </th>
  );
}

/** A sortable ticket table. The caller owns the th/td styling (the two tables
 *  differ: one is sticky inside a widget, the other sits in a modal) and the
 *  default row order. */
export function SortableTable<T>({
  rows,
  columns,
  rowKey,
  tableStyle,
  thStyle,
  tdStyle,
  emptyMessage,
}: {
  rows: T[];
  columns: Column<T>[];
  rowKey: (row: T, index: number) => string;
  tableStyle: CSSProperties;
  thStyle: CSSProperties;
  tdStyle: CSSProperties;
  emptyMessage: string;
}) {
  const { sorted, sort, toggle } = useColumnSort(rows, columns);
  return (
    <table style={tableStyle}>
      <thead>
        <tr>
          {columns.map((col) => (
            <SortHeader key={col.key} col={col} sort={sort} onToggle={toggle} style={thStyle} />
          ))}
        </tr>
      </thead>
      <tbody>
        {sorted.map((row, i) => (
          <tr key={rowKey(row, i)}>
            {columns.map((col) => (
              <td
                key={col.key}
                style={{
                  ...tdStyle,
                  ...(col.right ? { textAlign: "right" } : null),
                  ...col.cellStyle,
                }}
              >
                {col.cell(row)}
              </td>
            ))}
          </tr>
        ))}
        {sorted.length === 0 && (
          <tr>
            <td
              colSpan={columns.length}
              style={{ ...tdStyle, textAlign: "center", color: C.stone, padding: 26 }}
            >
              {emptyMessage}
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
}
